import { Box, Container, Typography, styled } from '@mui/material';
import React, { forwardRef } from 'react';

const SectionWrapper = styled(Box)(({ theme }) => ({
  padding: '96px 0',
  backgroundColor: '#FFFFFF',
}));

const FeatureRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: '48px',
  },
}));

const ReverseFeatureRow = styled(FeatureRow)(({ theme }) => ({
  flexDirection: 'row-reverse',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const FeatureContent = styled(Box)(({ theme }) => ({
  width: '45%',
  padding: '96px 64px 96px 0',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    padding: '48px 24px',
  },
}));

const ReverseFeatureContent = styled(FeatureContent)(({ theme }) => ({
  padding: '96px 0 96px 64px',
  [theme.breakpoints.down('md')]: {
    padding: '48px 24px',
  },
}));

const FeatureImage = styled(Box)(({ theme }) => ({
  width: '50%',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    height: '300px',
  },
}));

const FeatureIconWrapper = styled(Box)(({ theme }) => ({
  width: '56px',
  height: '56px',
  borderRadius: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#FFF4F3',
  border: '8px solid #FFE7E3',
  marginBottom: '24px',
}));

// Types for our features
interface Feature {
  id: string;
  title: string;
  description: string;
  image: string;
  reverse?: boolean;
}

// Define our features
const features: Feature[] = [
  {
    id: 'ai-communication',
    title: 'AI-Powered Communication',
    description:
      'Terr.ai handles tenant inquiries 24/7 by instantly drafting smart, human-like responses using AI—so you’re never starting from scratch. Every message, call, and email is logged and organized in one place, and you stay fully in control by reviewing and approving replies before they’re sent. It’s the perfect setup for single-property owners or anyone who can’t be available around the clock, but still wants to respond quickly and professionally.',
    image: '/assets/images/landing/feature-communication.png',
  },
  {
    id: 'automated-rent',
    title: 'Automated Rent Collection',
    description:
      'Automate your rent collection and take the pressure off your calendar. With Terr.ai, monthly rent is collected automatically, late payment reminders are sent out on your behalf, and receipts are generated without you lifting a finger — saving time, reducing awkward conversations, and eliminating payment headaches, especially for single-property owners.',
    image: '/assets/images/landing/feature-rent.jpg',
    reverse: true,
  },
  {
    id: 'portfolio-scale',
    title: 'From 1 unit to 10,000',
    description:
      'Whether you’re managing a single rental or an entire portfolio, Terr.ai scales with you. Our dashboard keeps everything organized, connected, and easy to manage — no hassle, no clutter, no matter how big you grow.',
    image: '/assets/images/landing/feature-units.jpg',
  },
  {
    id: 'lease-generation',
    title: 'Smart Lease Generation',
    description:
      'Easily generate leases tailored to your state’s guidelines using trusted third-party services like RocketLawyer. With customizable templates and built-in e-signature support, you can streamline lease creation while staying aligned with local regulations — all from one place.',
    image: '/assets/images/landing/feature-lease.png',
    reverse: true,
  },
  {
    id: 'property-calendar',
    title: 'Intelligent Property Calendar',
    description:
      'Stay ahead with our AI-powered calendar that tracks important dates and responsibilities—so nothing slips through the cracks. From lease renewals and warranty expirations to maintenance reminders and utility due dates, Terr.ai automatically parses uploaded lease documents to identify who’s responsible for what, then sends timely alerts to the right party—landlord, manager, or tenant.',
    image: '/assets/images/landing/feature-calendar.png',
  },
];

const Text = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  lineHeight: 1.6,
  color: '#667085',
  marginBottom: '16px',
}));

// Instead use type alias directly
type NewLandingFeaturesSectionProps = React.PropsWithRef<{}>;

const NewLandingFeaturesSection = forwardRef<HTMLDivElement, NewLandingFeaturesSectionProps>(
  (props, ref) => (
    <Box ref={ref}>
      <SectionWrapper>
        {/* Why Choose Us Heading Section */}
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="landingH2"
              component="h2"
              sx={{
                mb: 2,
                color: '#181D27',
                fontFamily: 'Nohemi',
                fontSize: { xs: '28px', md: '36px' },
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              Why Choose Us?
            </Typography>
            <Typography
              variant="landingBody"
              sx={{
                maxWidth: '800px',
                mx: 'auto',
                color: '#535862',
                fontSize: { xs: '16px', md: '20px' },
                textAlign: 'center',
              }}
            >
              <Text>
                <b>
                  Because your tenants don't want to learn a new system — they just want to be
                  heard.
                </b>
              </Text>
              <Text>
                Imagine this:
                <br />A tenant has a leaking faucet. They do what they've always done — send a quick
                text. No app. No login. No hoops to jump through.
              </Text>
              <Text>
                What they don't see is what happens next.
                <br />
                Behind the scenes, Terr.ai instantly reads the message, understands it's a
                maintenance request, logs it, and alerts the right person. You're looped in, the
                issue is handled, and the tenant gets a quick response — all without you lifting a
                finger.
              </Text>
              <Text>
                That's the power of AI working invisibly in the background. No change for them.
                Game-changer for you.
              </Text>
            </Typography>
          </Box>

          {/* Feature Rows */}
          {features.map(feature =>
            feature.reverse ? (
              <ReverseFeatureRow key={feature.id}>
                <ReverseFeatureContent>
                  <FeatureIconWrapper>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                        stroke="#9D1500"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </FeatureIconWrapper>
                  <Typography
                    variant="landingH3"
                    component="h3"
                    sx={{
                      mb: 2,
                      color: '#181D27',
                      fontFamily: 'Nohemi',
                      fontSize: { xs: '24px', md: '30px' },
                      fontWeight: 600,
                      lineHeight: 1.3,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="landingBody"
                    sx={{
                      color: '#535862',
                      fontSize: { xs: '16px', md: '18px' },
                      mb: 3,
                    }}
                  >
                    {feature.description}
                  </Typography>
                </ReverseFeatureContent>
                <FeatureImage
                  sx={{
                    backgroundImage: `url(${feature.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              </ReverseFeatureRow>
            ) : (
              <FeatureRow key={feature.id}>
                <FeatureContent>
                  <FeatureIconWrapper>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                        stroke="#9D1500"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </FeatureIconWrapper>
                  <Typography
                    variant="landingH3"
                    component="h3"
                    sx={{
                      mb: 2,
                      color: '#181D27',
                      fontFamily: 'Nohemi',
                      fontSize: { xs: '24px', md: '30px' },
                      fontWeight: 600,
                      lineHeight: 1.3,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="landingBody"
                    sx={{
                      color: '#535862',
                      fontSize: { xs: '16px', md: '18px' },
                      mb: 3,
                    }}
                  >
                    {feature.description}
                  </Typography>
                </FeatureContent>
                <FeatureImage
                  sx={{
                    backgroundImage: `url(${feature.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              </FeatureRow>
            )
          )}
        </Container>
      </SectionWrapper>
    </Box>
  )
);

NewLandingFeaturesSection.displayName = 'NewLandingFeaturesSection';

export default NewLandingFeaturesSection;
